import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate, formatMoney } from '@/lib/format';
import type { InvoiceLine, InvoiceStatus } from '@/types/platform';
import { Form } from '@inertiajs/react';
import { Check } from 'lucide-react';

interface PayClosed {
  mode: 'closed';
}

interface PayNone {
  mode: 'none';
}

interface PayDemo {
  mode: 'demo';
  action: string;
}

interface PayProvider {
  mode: 'provider';
  url: string;
  provider: 'stripe' | 'square' | null;
}

type Pay = PayClosed | PayNone | PayDemo | PayProvider;

interface PayLinkInvoice {
  number: string | null;
  status: InvoiceStatus;
  currency: string;
  memo: string | null;
  dueOn: string | null;
  paidAt: string | null;
  totalCents: number;
  lines: InvoiceLine[];
}

interface Seller {
  companyName: string;
  appName: string;
}

interface Props {
  invoice: PayLinkInvoice;
  seller: Seller;
  customerName: string;
  pay: Pay;
  // Read by the public layout, which shows the CAP Rock Labs mark in its footer.
  watermarked: boolean;
}

const PROVIDER_NAMES = { stripe: 'Stripe', square: 'Square' } as const;

interface PayAreaProps {
  pay: Pay;
  invoice: PayLinkInvoice;
  sellerName: string;
  total: string;
}

// What the customer can do about this invoice. One of four cases, decided by the server.
const PayArea = ({ pay, invoice, sellerName, total }: PayAreaProps) => {
  if (pay.mode === 'provider') {
    const providerName = pay.provider ? PROVIDER_NAMES[pay.provider] : null;

    return (
      <div className="flex flex-col gap-3">
        <Button asChild size="lg" className="w-full sm:w-fit">
          <a href={pay.url}>Pay {total}</a>
        </Button>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {providerName
            ? `This opens ${providerName} to take the payment.`
            : 'This opens the payment page.'}{' '}
          Your payment goes to {sellerName}.
        </p>
      </div>
    );
  } else if (pay.mode === 'demo') {
    return (
      <div className="flex flex-col gap-3 border border-dashed p-4">
        <p className="font-medium">Demo payment. No card is charged.</p>
        <Form method="post" action={pay.action}>
          {({ processing }) => (
            <Button type="submit" size="lg" className="w-full sm:w-fit" disabled={processing}>
              Pay {total} (demo)
            </Button>
          )}
        </Form>
      </div>
    );
  } else if (pay.mode === 'none') {
    return <p className="leading-relaxed">Contact {sellerName} to arrange payment.</p>;
  } else if (invoice.status === 'paid') {
    return (
      <p className="flex items-center gap-2 text-lg font-medium text-green-800">
        <Check className="size-5 shrink-0" aria-hidden="true" />
        {invoice.paidAt ? `Paid on ${formatDate(invoice.paidAt)}` : 'Paid'}
      </p>
    );
  } else {
    return <p className="leading-relaxed">This invoice is {invoice.status}.</p>;
  }
};

const Show = ({ invoice, seller, customerName, pay }: Props) => {
  const money = (cents: number) => formatMoney(cents, invoice.currency);
  const total = money(invoice.totalCents);

  return (
    <article>
      <header className="flex flex-col gap-2">
        <h1 className="authority-display text-4xl leading-tight break-words">
          {seller.companyName}
        </h1>
        <p className="text-muted-foreground">
          {invoice.number ? `Invoice ${invoice.number}` : 'Invoice'} for {customerName}
        </p>
      </header>

      <div className="mt-8 border-t">
        {invoice.lines.length > 0 ? (
          <Table>
            <TableCaption className="sr-only">Line items</TableCaption>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead scope="col" className="text-muted-foreground pl-0 font-normal">
                  Description
                </TableHead>
                <TableHead
                  scope="col"
                  className="text-muted-foreground hidden text-right font-normal sm:table-cell"
                >
                  Qty × price
                </TableHead>
                <TableHead
                  scope="col"
                  className="text-muted-foreground pr-0 text-right font-normal"
                >
                  Amount
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoice.lines.map((line, index) => {
                const quantityAndPrice = `${line.quantity} × ${money(line.unitAmountCents)}`;

                return (
                  <TableRow key={line.id ?? index} className="hover:bg-transparent">
                    <TableCell className="py-3 pl-0 align-top whitespace-normal">
                      <span className="break-words">{line.description}</span>
                      {/* Under 640px the middle column is folded in here so the row fits a phone. */}
                      <span className="text-muted-foreground mt-0.5 block text-xs tabular-nums sm:hidden">
                        {quantityAndPrice}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground hidden py-3 text-right align-top tabular-nums sm:table-cell">
                      {quantityAndPrice}
                    </TableCell>
                    <TableCell className="py-3 pr-0 text-right align-top tabular-nums">
                      {money(line.quantity * line.unitAmountCents)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : null}

        <dl className={invoice.lines.length > 0 ? 'border-t' : undefined}>
          <div className="flex items-baseline justify-between gap-6 border-b py-4">
            <dt className="font-medium">Total</dt>
            <dd className="text-2xl font-medium tabular-nums">{total}</dd>
          </div>
          {invoice.dueOn ? (
            <div className="flex items-baseline justify-between gap-6 border-b py-3 text-sm">
              <dt className="text-muted-foreground">Due</dt>
              <dd>{formatDate(invoice.dueOn)}</dd>
            </div>
          ) : null}
        </dl>
      </div>

      {invoice.memo ? (
        <section aria-labelledby="invoice-memo" className="mt-6">
          <h2 id="invoice-memo" className="text-muted-foreground text-sm">
            Note from {seller.companyName}
          </h2>
          <p className="mt-1 leading-relaxed break-words whitespace-pre-line">{invoice.memo}</p>
        </section>
      ) : null}

      <section aria-labelledby="invoice-payment" className="mt-8">
        <h2 id="invoice-payment" className="sr-only">
          Payment
        </h2>
        <PayArea pay={pay} invoice={invoice} sellerName={seller.companyName} total={total} />
      </section>
    </article>
  );
};

export default Show;
